/*
 *
 * Hedera Stablecoin SDK
 *
 * Copyright (C) 2023 Hedera Hashgraph, LLC
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *      http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 *
 */
var __decorate = (this && this.__decorate) || function (decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for (var i = decorators.length - 1; i >= 0; i--) if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
};
var __metadata = (this && this.__metadata) || function (k, v) {
    if (typeof Reflect === "object" && typeof Reflect.metadata === "function") return Reflect.metadata(k, v);
};
/* eslint-disable @typescript-eslint/no-unused-vars */
import Injectable from '../../core/Injectable.js';
import { QueryBus } from '../../core/query/QueryBus.js';
import { GetAccountInfoRequest, GetListStableCoinRequest, } from './request/index.js';
import { GetListStableCoinQuery } from '../../app/usecase/query/stablecoin/list/GetListStableCoinQuery.js';
import GetPublicKeyRequest from './request/GetPublicKeyRequest.js';
import PublicKey from '../../domain/context/account/PublicKey.js';
import { default as HederaAccount } from '../../domain/context/account/Account.js';
import { HederaId } from '../../domain/context/shared/HederaId.js';
import { GetAccountInfoQuery } from '../../app/usecase/query/account/info/GetAccountInfoQuery.js';
import { InvalidResponse } from '../out/mirror/error/InvalidResponse.js';
import { handleValidation } from './Common.js';
import { LogError } from '../../core/decorator/LogErrorDecorator.js';
class AccountInPort {
    constructor(queryBus = Injectable.resolve(QueryBus)) {
        this.queryBus = queryBus;
        this.NullHederaAccount = HederaAccount.NULL;
        this.NullPublicKey = PublicKey.NULL;
    }
    async getPublicKey(request) {
        handleValidation('GetPublicKeyRequest', request);
        const res = await this.queryBus.execute(new GetAccountInfoQuery(HederaId.from(request.account.accountId)));
        if (!res.account.publicKey)
            throw new InvalidResponse(`No public key for account ${res.account.id ??
                res.account.alias ??
                res.account.accountEvmAddress}`);
        return res.account.publicKey;
    }
    async listStableCoins(request) {
        handleValidation('GetListStableCoinRequest', request);
        return (await this.queryBus.execute(new GetListStableCoinQuery(HederaId.from(request.account.accountId)))).list;
    }
    async getInfo(request) {
        handleValidation('GetAccountInfoRequest', request);
        return (await this.queryBus.execute(new GetAccountInfoQuery(HederaId.from(request.account.accountId)))).account;
    }
}
__decorate([
    LogError,
    __metadata("design:type", Function),
    __metadata("design:paramtypes", [GetPublicKeyRequest]),
    __metadata("design:returntype", Promise)
], AccountInPort.prototype, "getPublicKey", null);
__decorate([
    LogError,
    __metadata("design:type", Function),
    __metadata("design:paramtypes", [GetListStableCoinRequest]),
    __metadata("design:returntype", Promise)
], AccountInPort.prototype, "listStableCoins", null);
__decorate([
    LogError,
    __metadata("design:type", Function),
    __metadata("design:paramtypes", [GetAccountInfoRequest]),
    __metadata("design:returntype", Promise)
], AccountInPort.prototype, "getInfo", null);
const Account = new AccountInPort();
export default Account;
